/**
 * LinkifyTextCustomField - URL-only text linkification for custom fields
 *
 * WHY THIS EXISTS:
 * This component was built specifically for rendering custom field values where we
 * want to make URLs clickable WITHOUT interpreting any other text formatting.
 *
 * WHEN TO USE THIS vs linkifyString (from ./linkify-string.jsx):
 *
 * Use LinkifyTextCustomField when:
 * - You have plain text that may contain URLs (e.g., custom field values)
 * - You want ONLY URLs to be clickable, with all other text unchanged
 * - The text might contain characters that could be misinterpreted as markdown
 *   (e.g., asterisks, underscores, backticks, hash symbols)
 * - Example: "Price is $100 * 2 = $200, see www.example.com" renders correctly
 *
 * Use linkifyString when:
 * - You want full markdown rendering WITH automatic URL detection
 * - The text is expected to contain markdown formatting (bold, italics, etc.)
 * - You're displaying user-authored content where markdown is intentional
 *
 * URL PATTERN:
 * This component only linkifies URLs that match the PHP Linkifier pattern
 * (see symfony/src/Nutshell/StringsBundle/Formatter/Linkifier.php):
 * - URLs starting with a scheme (http://, https://, ftp://, etc.)
 * - URLs starting with www.
 *
 * Bare domains like "example.com" are NOT linkified to avoid false positives
 * (e.g., "meeting at 3.pm" should not create a link).
 */

import * as React from 'react';

import {find as findLinks} from 'linkifyjs';

import styles from './linkify-text-custom-field.module.css';

// Pattern to match URLs that start with a scheme (http://, https://, etc.) or www.
// This matches the PHP Linkifier pattern used elsewhere in the codebase
const VALID_URL_PATTERN = /^(?:[a-z0-9+.-]+:\/\/|www\.)/i;

type LinkInfo = {
    value: string;
    href: string;
};

/**
 * Filters linkifyjs results to only include URLs that match our pattern:
 * - URLs starting with a scheme (http://, https://, etc.)
 * - URLs starting with www.
 */
function getFilteredLinks(text: string): LinkInfo[] {
    const links = findLinks(text);

    return links
        .filter((link) => VALID_URL_PATTERN.test(link.value))
        .map((link) => ({
            value: link.value,
            href: link.href,
        }));
}

/**
 * Splits text by links and returns an array of segments.
 * Each segment is either a plain text string or a link object.
 */
type Segment =
    | {
          type: 'text';
          value: string;
          key: string;
      }
    | {
          type: 'link';
          value: string;
          href: string;
          key: string;
      };

function splitTextByLinks(text: string, links: LinkInfo[]): Segment[] {
    if (links.length === 0) {
        return [{type: 'text', value: text, key: 'text-0'}];
    }

    const segments: Segment[] = [];
    let remainingText = text;
    let segmentIndex = 0;

    // Process each link in order of appearance
    for (const link of links) {
        const linkIndex = remainingText.indexOf(link.value);
        if (linkIndex !== -1) {
            // Add text before the link
            if (linkIndex > 0) {
                segments.push({
                    type: 'text',
                    value: remainingText.substring(0, linkIndex),
                    key: `text-${segmentIndex}`,
                });
                segmentIndex += 1;
            }

            // Add the link
            segments.push({
                type: 'link',
                value: link.value,
                href: link.href,
                key: `link-${segmentIndex}`,
            });
            segmentIndex += 1;

            // Update remaining text
            remainingText = remainingText.substring(linkIndex + link.value.length);
        }
    }

    // Add any remaining text after the last link
    if (remainingText.length > 0) {
        segments.push({type: 'text', value: remainingText, key: `text-${segmentIndex}`});
    }

    return segments;
}

/**
 * Renders text with URLs converted to clickable links.
 * Only URLs starting with a scheme (http://, https://) or www. are linkified.
 * Links are underlined and open in a new tab.
 */
export function LinkifyTextCustomField({
    text,
    displayText,
}: {
    text: string;
    displayText?: string;
}): React.ReactElement {
    const links = getFilteredLinks(text);

    const singleLink = links.length === 1 ? links[0] : null;
    const trimmedText = text.trim();
    const trimmedDisplayText = displayText?.trim();
    const shouldRenderDisplayTextAsSingleLink =
        Boolean(trimmedDisplayText) &&
        Boolean(singleLink) &&
        trimmedText === singleLink?.value.trim();

    if (links.length === 0) {
        return <>{displayText ?? text}</>;
    }

    const segments = splitTextByLinks(text, links);

    return (
        <>
            {segments.map((segment) => {
                if (segment.type === 'text') {
                    return <React.Fragment key={segment.key}>{segment.value}</React.Fragment>;
                } else {
                    return (
                        <a
                            key={segment.key}
                            href={segment.href}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={styles.link}
                            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                                e.stopPropagation()
                            }
                        >
                            {shouldRenderDisplayTextAsSingleLink ? displayText : segment.value}
                        </a>
                    );
                }
            })}
        </>
    );
}
